import { Link } from 'react-router-dom';
import type { TeamSlot } from '../lib/types';
import { Bar, Button } from './ui';
import { dexNumber, genderSuffix } from '../lib/format';

const TEAM_SIZE = 6;

/**
 * My Team.
 *
 * The first thing a visitor should notice, so it gets the largest cards on the
 * page and always renders six slots — an empty one reads as an invitation
 * rather than as a shorter list. What is shown is what the *owner chose*, never
 * a sample of whatever happens to be standing in a Grove.
 */
export function TeamPanel({
  slots,
  isSelf,
  onEdit,
  onFeed,
  fedIds,
  busyId,
}: {
  slots: TeamSlot[];
  isSelf: boolean;
  onEdit?: () => void;
  /** Omitted when the viewer cannot feed here at all (e.g. an NPC's profile). */
  onFeed?: (pokemon: TeamSlot['pokemon']) => void;
  /** Already fed within the cooldown window. */
  fedIds?: Set<string>;
  busyId?: string | null;
}) {
  const byPosition = new Map(slots.map((slot) => [slot.position, slot]));

  return (
    <div className="team-panel">
      {Array.from({ length: TEAM_SIZE }, (_, position) => {
        const slot = byPosition.get(position);

        if (!slot) {
          return isSelf ? (
            <button
              key={position}
              type="button"
              className="pxwell team-card team-card--empty"
              onClick={onEdit}
              aria-label={`Add a Pokémon to Team slot ${position + 1}`}
            >
              <span className="team-card__plus" aria-hidden>
                +
              </span>
              <span className="tiny muted">Add a favourite</span>
            </button>
          ) : (
            <div key={position} className="pxwell team-card team-card--empty" aria-hidden>
              <span className="team-card__plus">·</span>
            </div>
          );
        }

        const pokemon = slot.pokemon;
        const gender = genderSuffix(pokemon.displayName, pokemon.gender);
        const fed = fedIds?.has(pokemon.id) ?? false;

        return (
          /* A div, not a Link: the card carries its own Feed button, and a
             button nested inside an anchor is invalid markup that navigates
             instead of feeding. The link covers the sprite and name only. */
          <div
            key={position}
            className={`pxframe team-card ${pokemon.isShiny ? 'team-card--shiny' : ''}`}
          >
            <Link to={`/pokemon/${pokemon.id}`} className="team-card__link">
              <div className="team-card__stage">
                {pokemon.isShiny && (
                  <span className="shiny-star" aria-hidden>
                    ✦
                  </span>
                )}
                <img className="pixel team-card__sprite" src={pokemon.sprite} alt="" />
              </div>

              <div className="team-card__name">
                {pokemon.displayName}
                {gender && <span className="muted"> {gender}</span>}
              </div>

              {/* The species is shown under a nickname rather than instead of it
                  — "Pip" tells a visitor nothing on its own, and hiding the
                  species behind a hover would make half the Teams
                  unreadable. */}
              <div className="tiny muted mono">
                {pokemon.nickname ? `${pokemon.species.displayName} · ` : ''}
                {dexNumber(pokemon.species.id)}
              </div>

              <div className="team-card__meta">
                <span className="chip chip--sm">Lv {pokemon.level}</span>
                {pokemon.isShiny && <span className="chip chip--sm chip--shiny">✦ Shiny</span>}
              </div>
            </Link>

            <div className="team-card__friendship" title={`Friendship ${pokemon.friendship}/255`}>
              <span className="tiny muted">♥ {pokemon.friendship}</span>
              <Bar ratio={pokemon.friendship / 255} variant="ready" />
            </div>

            {onFeed && (
              <Button
                size="sm"
                block
                variant={fed ? 'default' : 'go'}
                disabled={fed || busyId === pokemon.id}
                loading={busyId === pokemon.id}
                onClick={() => onFeed(pokemon)}
              >
                {fed ? '✓ Fed' : isSelf ? 'Feed' : '♥ Feed'}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
